import { useState } from "react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useRecordContext,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

import type { Company } from "../types";
import {
  BARREL_BASES,
  COMPANY_KINDS,
  CUSTOMER_TYPES,
  basisPatch,
  customerTypePatch,
  describeBarrels,
  describeBasis,
  discardsBarrelCount,
  impliedBasis,
  invalidateCompanyProfile,
  kindPatch,
  saveCompanyProfile,
  validateBarrelCount,
  type CompanyProfilePatch,
} from "./companyProfile";
import type {
  BarrelBasis,
  CompanyKind,
  CompanyProfileRow,
  CustomerType,
} from "./types";

/**
 * What this company is to us, and how big: ops#2's four facts, on the company
 * page.
 *
 * WHY IT READS ITS OWN TABLE AND NOT THE COMPANY RECORD
 * ------------------------------------------------------
 * These columns are not on `companies` and deliberately never will be. That
 * is upstream's table -- CompanyShow.tsx alone took 18 upstream commits in
 * twelve months -- and the data provider rewrites getOne("companies") to
 * `companies_summary`, an upstream view that lists its columns explicitly and
 * knows nothing about ours. A column there is also a column the company FORM
 * round-trips whether or not anybody touched it. So: a separate table keyed
 * on company_id, read here directly. See migration 069.
 *
 * WHY EVERY CONTROL WRITES ON CHANGE, WITH NO SAVE BUTTON
 * --------------------------------------------------------
 * Same shape as LifecyclePanel, and for the same reason: this is not a form
 * with a draft state, it is four facts that are either recorded or not. A
 * Save button here would add a state in which the screen and the database
 * disagree -- and the one destructive change available (dropping a barrel
 * count by making the company a partner) is confirmed explicitly instead,
 * which is a better guard than a Save button that also protects the harmless
 * changes nobody needed protecting from.
 *
 * WHAT THE PANEL EXISTS TO PREVENT
 * ---------------------------------
 * A barrel count read without its basis. A distillery's is a lifetime total
 * and an NDP's is barrels a year, so the number alone is two different
 * companies rendered identically. Every count on this panel comes from
 * describeBarrels(), which cannot return one without the other, and the input
 * that edits it is labeled with the basis rather than "Barrel count".
 */
export const CompanyProfilePanel = () => {
  const company = useRecordContext<Company>();
  const companyId = company?.id;
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const notify = useNotify();

  const {
    data: rows,
    isPending,
    error,
  } = useGetList<CompanyProfileRow>(
    "company_profile",
    {
      filter: { company_id: companyId },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "company_id", order: "ASC" },
    },
    { enabled: companyId != null },
  );

  /*
   * NOT `?? {}`. A company with no profile row is the normal state -- 57
   * companies have none -- and it has to stay distinguishable from a row
   * whose columns happen to be null, because the first write for the former
   * is an INSERT and for the latter an update.
   */
  const profile = rows?.[0] ?? null;

  const [countDraft, setCountDraft] = useState<string | null>(null);
  const [countError, setCountError] = useState<string | null>(null);
  /* The kind waiting on a confirmation, because saving it discards a count. */
  const [discarding, setDiscarding] = useState<CompanyKind | null>(null);
  const [busy, setBusy] = useState(false);

  if (!company) return null;

  const write = async (patch: CompanyProfilePatch) => {
    setBusy(true);
    try {
      await saveCompanyProfile(dataProvider, company.id, patch);
      await invalidateCompanyProfile(queryClient);
      return true;
    } catch (e) {
      notify((e as Error).message ?? "Could not save the profile", {
        type: "error",
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  /*
   * NOTHING IS CLICKABLE UNTIL THE ROW HAS ARRIVED, and this is not
   * cosmetic. chooseKind decides whether to warn by looking at the barrel
   * count on the row; a click that lands while the read is still in flight
   * sees `profile === null`, finds no count to warn about, and discards one
   * silently. The browser test found exactly that, by clicking a fraction
   * too early.
   */
  const settling = busy || isPending;

  const kind = profile?.kind ?? null;
  const customerType = profile?.customer_type ?? null;
  const basis = profile?.barrel_count_basis ?? null;
  const forcedBasis = impliedBasis(customerType);

  const chooseKind = async (next: CompanyKind) => {
    /*
     * ASK FIRST. The write itself is correct either way -- kindPatch clears
     * the customer-only columns because three CHECK constraints require it --
     * but a barrel count takes a conversation to get and nothing in the app
     * can bring it back.
     */
    if (discardsBarrelCount(profile, next)) {
      setDiscarding(next);
      return;
    }
    if (await write(kindPatch(next)))
      notify(`Recorded as ${next}.`, { type: "success" });
  };

  const confirmDiscard = async () => {
    if (!discarding) return;
    const next = discarding;
    if (await write(kindPatch(next))) {
      setDiscarding(null);
      notify(`Recorded as ${next}. The barrel count is gone.`, {
        type: "success",
      });
    }
  };

  const chooseCustomerType = async (next: CustomerType) => {
    let patch;
    try {
      // Throws when the company is not a customer, and carries the basis the
      // type forces. The two columns move together or the update is rejected.
      patch = customerTypePatch(next, { kind, basis });
    } catch (e) {
      notify((e as Error).message, { type: "error" });
      return;
    }
    if (await write(patch)) {
      setCountError(null);
      notify(`Customer type set to ${next}.`, { type: "success" });
    }
  };

  const chooseBasis = async (next: BarrelBasis) => {
    let patch;
    try {
      // Throws on a basis the customer type contradicts. The control below is
      // hidden for distilleries and NDPs, but a hidden control is still one
      // something else could fire.
      patch = basisPatch(next, customerType);
    } catch (e) {
      notify((e as Error).message, { type: "error" });
      return;
    }
    if (await write(patch)) setCountError(null);
  };

  const applyCount = async (raw: string) => {
    const check = validateBarrelCount(raw, { kind, customerType, basis });
    if (!check.ok) {
      // Inline, not only a toast. The reason belongs next to the field that
      // caused it, and a toast is gone before anyone re-reads the number.
      setCountError(check.reason);
      return;
    }
    setCountError(null);
    if (await write(check.patch)) {
      setCountDraft(null);
      notify(
        check.patch.barrel_count == null
          ? "Barrel count cleared."
          : // The basis is in the confirmation too: a toast saying "12,000
            // barrels" would be the one place on this screen the number
            // appeared without saying what it counts. The estimate flag
            // falls back to TRUE rather than to undefined -- that is the
            // column's default, so a first insert is an estimate, and a
            // toast reading "confirmed" would be the write describing
            // itself wrongly.
            describeBarrels({
              barrel_count: check.patch.barrel_count ?? null,
              barrel_count_basis:
                check.patch.barrel_count_basis ??
                profile?.barrel_count_basis ??
                null,
              barrel_count_is_estimate:
                profile?.barrel_count_is_estimate ?? true,
            }),
        { type: "success" },
      );
    }
  };

  return (
    <div className="mt-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">Profile</h3>
        <span className="text-xs text-muted-foreground">
          {/* Silent while loading: "nothing recorded" is a claim, and a claim
              must not be made from an answer we do not have yet. */}
          {isPending || error ? null : profile ? null : "Nothing recorded yet"}
        </span>
      </div>
      <Separator className="mt-1.5" />

      {error ? (
        <p className="mt-3 text-sm text-destructive">
          Could not read this company&apos;s profile, so this is not a record of
          a company we know nothing about — it is a record of not knowing.
        </p>
      ) : (
        <div className="mt-3 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="company-profile-kind">Type</Label>
              <Select
                value={kind ?? undefined}
                onValueChange={(v) => chooseKind(v as CompanyKind)}
                disabled={settling}
              >
                <SelectTrigger id="company-profile-kind" className="w-40">
                  <SelectValue placeholder="Not recorded" />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_KINDS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>
                      {k.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/*
              CUSTOMER-ONLY, and structurally so: company_profile_customer_type_scope
              rejects a customer type on anything else. Hiding it is the first
              guard; customerTypePatch throwing is the second.
            */}
            {kind === "customer" && (
              <div className="space-y-1.5">
                <Label htmlFor="company-profile-customer-type">
                  Customer type
                </Label>
                <Select
                  value={customerType ?? undefined}
                  onValueChange={(v) => chooseCustomerType(v as CustomerType)}
                  disabled={settling}
                >
                  <SelectTrigger
                    id="company-profile-customer-type"
                    className="w-64"
                  >
                    <SelectValue placeholder="Choose a customer type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CUSTOMER_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {discarding ? (
            <DiscardConfirm
              nextKind={discarding}
              said={describeBarrels(profile)}
              busy={busy}
              onConfirm={confirmDiscard}
              onCancel={() => setDiscarding(null)}
            />
          ) : null}

          {kind === "customer" ? (
            <Barrels
              profile={profile}
              customerType={customerType}
              basis={basis}
              forcedBasis={forcedBasis}
              draft={countDraft ?? String(profile?.barrel_count ?? "")}
              onDraft={(v) => {
                setCountDraft(v);
                setCountError(null);
              }}
              onApply={applyCount}
              onBasis={chooseBasis}
              onEstimate={(isEstimate) =>
                write({ barrel_count_is_estimate: isEstimate })
              }
              error={countError}
              busy={settling}
            />
          ) : kind ? (
            /*
              Said out loud rather than left as a blank space. A partner has no
              barrel count BY CONSTRUCTION -- barrels_scope -- and an empty
              area under the picker would read as a field nobody had got round
              to filling in.
            */
            <p className="text-sm text-muted-foreground">
              Barrel counts are recorded for customers only. Nothing is being
              hidden here — the column is scoped to a customer, so there is
              nothing to hide.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nobody has said what this company is to us yet. Type is the first
              question: everything else on this panel depends on the answer.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * The confirmation, before a barrel count is thrown away.
 *
 * IT NAMES THE NUMBER IT IS ABOUT TO DISCARD, with its basis, because "are
 * you sure?" over a company whose count nobody remembers and over one holding
 * a figure somebody spent a phone call establishing are different decisions.
 * There is no history table for company_profile, so this is irreversible in
 * the only sense that matters: the number is not written down anywhere else.
 */
const DiscardConfirm = ({
  nextKind,
  said,
  busy,
  onConfirm,
  onCancel,
}: {
  nextKind: CompanyKind;
  said: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div className="rounded-md border-2 border-destructive p-4 space-y-2">
    <h4 className="text-sm font-semibold text-destructive">
      Making this company a {nextKind} discards its barrel count.
    </h4>
    <p className="max-w-3xl text-sm text-muted-foreground">
      {said} That number is only stored here, and the database scopes it to a
      customer — so recording this company as a {nextKind} clears the count, the
      customer type and the basis in the same write. Nothing in the app can
      bring them back.
    </p>
    <div className="flex gap-2">
      <Button variant="destructive" onClick={onConfirm} disabled={busy}>
        Yes, discard the count
      </Button>
      <Button variant="ghost" onClick={onCancel} disabled={busy}>
        Keep it a customer
      </Button>
    </div>
  </div>
);

/**
 * The barrel count: the number, what it counts, and how sure anybody is.
 *
 * THE FIELD IS LABELED WITH THE BASIS, not "Barrel count". A label that said
 * only "Barrel count" would put a bare number on screen in the one place a
 * person types one, which is precisely where the distinction between a
 * lifetime total and a yearly run has to be visible.
 *
 * THE BASIS IS A CONTROL ONLY WHEN IT IS A REAL QUESTION. A distillery is
 * total and an NDP is annual by CHECK constraint, so offering those a picker
 * would be offering a write the database refuses. "Other" implies nothing, so
 * there it is a genuine choice and the panel asks for it -- and refuses a
 * count until it has an answer.
 */
const Barrels = ({
  profile,
  customerType,
  basis,
  forcedBasis,
  draft,
  onDraft,
  onApply,
  onBasis,
  onEstimate,
  error,
  busy,
}: {
  profile: CompanyProfileRow | null;
  customerType: CustomerType | null;
  basis: BarrelBasis | null;
  forcedBasis: BarrelBasis | null;
  draft: string;
  onDraft: (value: string) => void;
  onApply: (value: string) => void;
  onBasis: (basis: BarrelBasis) => void;
  onEstimate: (isEstimate: boolean) => void;
  error: string | null;
  busy: boolean;
}) => {
  const effective = forcedBasis ?? basis;
  const label =
    effective === "total"
      ? "Barrels held in total"
      : effective === "annual"
        ? "Barrels a year"
        : "Barrel count";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm">
          <strong>{describeBarrels(profile)}</strong>
        </p>
        {profile?.barrel_count != null ? (
          /*
            ESTIMATED IS THE DEFAULT AND READS AS AN ANSWER. A quiet outline
            badge for the ordinary case, a solid one for a figure somebody
            actually confirmed -- different enough to tell apart across a room,
            without making the common case look like an unfinished field. A
            panel that made "estimated" look like missing data would push
            people to mark guesses as confirmed just to tidy the screen, and
            then someone sizes a deal on hearsay.
          */
          <Badge
            variant={profile.barrel_count_is_estimate ? "outline" : "default"}
          >
            {profile.barrel_count_is_estimate ? "estimated" : "confirmed"}
          </Badge>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="company-profile-barrels">{label}</Label>
          <Input
            id="company-profile-barrels"
            type="number"
            min={0}
            className="w-36"
            value={draft}
            onChange={(e) => onDraft(e.target.value)}
            disabled={busy}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onApply(draft)}
          disabled={busy}
        >
          Apply
        </Button>

        {/*
          ONLY FOR A CUSTOMER TYPE THAT IMPLIES NOTHING. This is the third
          type, "other", and the reason barrel_count_basis is a stored column
          rather than something derived from the customer type.
        */}
        {forcedBasis ? null : (
          <div className="space-y-1.5">
            <Label htmlFor="company-profile-basis">Counts as</Label>
            <Select
              value={basis ?? undefined}
              onValueChange={(v) => onBasis(v as BarrelBasis)}
              disabled={busy}
            >
              <SelectTrigger id="company-profile-basis" className="w-64">
                <SelectValue placeholder="Total or a year?" />
              </SelectTrigger>
              <SelectContent>
                {BARREL_BASES.map((b) => (
                  <SelectItem key={b.value} value={b.value}>
                    {b.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="company-profile-estimate">This figure is</Label>
          <Select
            value={
              profile?.barrel_count_is_estimate === false
                ? "confirmed"
                : "estimated"
            }
            onValueChange={(v) => onEstimate(v === "estimated")}
            disabled={busy}
          >
            <SelectTrigger id="company-profile-estimate" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="estimated">Estimated</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {describeBasis(basis, customerType)} Estimated is the honest default —
          these numbers arrive from conversations, and marking a guess as
          confirmed is how someone ends up sizing a deal on hearsay.
        </p>
      )}
    </div>
  );
};
